/**
 * EmptyState Component
 *
 * Modern empty state with illustrations and helpful CTAs.
 * Supports different variants for each content type.
 * Enhanced with better visual hierarchy and actionable guidance.
 */

import PropTypes from "prop-types";
import { motion } from "framer-motion";
import Icon from "@mui/material/Icon";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";

// Animation variants
const containerVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.5,
      ease: "easeOut",
      staggerChildren: 0.12,
    },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 10 },
  visible: { opacity: 1, y: 0 },
};

const iconVariants = {
  hidden: { scale: 0.8, opacity: 0 },
  visible: {
    scale: 1,
    opacity: 1,
    transition: {
      type: "spring",
      stiffness: 200,
      damping: 15,
    },
  },
  float: {
    y: [-5, 5, -5],
    transition: {
      duration: 3,
      repeat: Infinity,
      ease: "easeInOut",
    },
  },
};

// Content configurations for different empty states with enhanced messaging
const emptyStateConfig = {
  rss: {
    icon: "satellite_alt",
    iconColor: "#1A73E8",
    bgColor: "linear-gradient(135deg, rgba(26, 115, 232, 0.12) 0%, rgba(26, 115, 232, 0.04) 100%)",
    borderColor: "rgba(26, 115, 232, 0.2)",
    title: "Waiting for Fresh Content",
    subtitle: "Your feeds are being monitored",
    description:
      "New articles and updates will appear here automatically as your RSS sources publish. Check back soon or refresh to check for updates.",
    actionText: "Check for Updates",
    actionIcon: "refresh",
    actionColor: "info",
    tip: "💡 Tip: Content typically syncs every 15 minutes",
  },
  drafts: {
    icon: "auto_awesome",
    iconColor: "#FB8C00",
    bgColor: "linear-gradient(135deg, rgba(251, 140, 0, 0.12) 0%, rgba(251, 140, 0, 0.04) 100%)",
    borderColor: "rgba(251, 140, 0, 0.2)",
    title: "No Drafts Pending Review",
    subtitle: "You're all caught up! 🎉",
    description:
      "Select content from the RSS feed to generate AI-powered drafts, or create a custom post from scratch.",
    actionText: "Create New Post",
    actionIcon: "add_circle",
    actionColor: "warning",
    tip: "💡 Tip: AI-generated drafts include blog, social posts & images",
  },
  posted: {
    icon: "rocket_launch",
    iconColor: "#4CAF50",
    bgColor: "linear-gradient(135deg, rgba(76, 175, 80, 0.12) 0%, rgba(76, 175, 80, 0.04) 100%)",
    borderColor: "rgba(76, 175, 80, 0.2)",
    title: "Ready to Launch?",
    subtitle: "No published posts yet",
    description:
      "Your approved content will appear here with links to each platform. Head to Drafts to review and publish your first post!",
    actionText: "Review Drafts",
    actionIcon: "arrow_forward",
    actionColor: "success",
    tip: "💡 Tip: Published posts include LinkedIn, X, and Threads links",
  },
  rejected: {
    icon: "inventory_2",
    iconColor: "#7B809A",
    bgColor:
      "linear-gradient(135deg, rgba(123, 128, 154, 0.12) 0%, rgba(123, 128, 154, 0.04) 100%)",
    borderColor: "rgba(123, 128, 154, 0.2)",
    title: "Archive is Empty",
    subtitle: "No rejected posts",
    description:
      "Posts you decline are stored here for future reference. You can always revisit and regenerate content from archived items.",
    actionText: null,
    tip: "💡 Tip: Rejected posts can be edited and re-submitted",
  },
  search: {
    icon: "search_off",
    iconColor: "#7B809A",
    bgColor:
      "linear-gradient(135deg, rgba(123, 128, 154, 0.08) 0%, rgba(123, 128, 154, 0.02) 100%)",
    borderColor: "rgba(123, 128, 154, 0.15)",
    title: "No Matches Found",
    subtitle: "Try different search terms",
    description:
      "We couldn't find content matching your filters. Try broadening your search or clearing filters to see all items.",
    actionText: "Clear All Filters",
    actionIcon: "filter_alt_off",
    actionColor: "secondary",
    tip: null,
  },
};

function EmptyState({ variant = "rss", onAction }) {
  const config = emptyStateConfig[variant] || emptyStateConfig.rss;

  return (
    <MDBox
      component={motion.div}
      variants={containerVariants}
      initial="hidden"
      animate="visible"
      display="flex"
      justifyContent="center"
      alignItems="center"
      minHeight="420px"
      py={6}
    >
      <MDBox
        textAlign="center"
        maxWidth="420px"
        px={3}
        sx={{
          backgroundColor: "white",
          borderRadius: "24px",
          p: { xs: 4, sm: 5 },
          border: `1px solid ${config.borderColor}`,
          boxShadow: "0 4px 24px rgba(0, 0, 0, 0.04)",
        }}
      >
        {/* Animated Icon Container */}
        <MDBox
          component={motion.div}
          variants={iconVariants}
          animate={["visible", "float"]}
          sx={{
            width: 100,
            height: 100,
            borderRadius: "28px",
            background: config.bgColor,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            margin: "0 auto",
            mb: 3,
            border: `1px solid ${config.borderColor}`,
          }}
        >
          <Icon
            sx={{
              fontSize: "48px !important",
              color: config.iconColor,
            }}
          >
            {config.icon}
          </Icon>
        </MDBox>

        {/* Subtitle */}
        {config.subtitle && (
          <MDBox component={motion.div} variants={itemVariants}>
            <MDTypography
              variant="caption"
              sx={{
                color: config.iconColor,
                fontWeight: 600,
                textTransform: "uppercase",
                letterSpacing: "1px",
                fontSize: "0.7rem",
              }}
            >
              {config.subtitle}
            </MDTypography>
          </MDBox>
        )}

        {/* Title */}
        <MDBox component={motion.div} variants={itemVariants}>
          <MDTypography
            variant="h4"
            fontWeight="bold"
            mt={1}
            mb={1.5}
            sx={{
              color: "#1a1a2e",
              fontSize: { xs: "1.35rem", sm: "1.5rem" },
            }}
          >
            {config.title}
          </MDTypography>
        </MDBox>

        {/* Description */}
        <MDBox component={motion.div} variants={itemVariants}>
          <MDTypography
            variant="body2"
            color="text"
            mb={3}
            sx={{
              lineHeight: 1.7,
              color: "#4a4a68",
              fontSize: "0.9rem",
            }}
          >
            {config.description}
          </MDTypography>
        </MDBox>

        {/* Action Button */}
        {config.actionText && onAction && (
          <MDBox component={motion.div} variants={itemVariants}>
            <MDButton
              variant="gradient"
              color={config.actionColor || "info"}
              startIcon={config.actionIcon ? <Icon>{config.actionIcon}</Icon> : null}
              onClick={onAction}
              sx={{
                px: 4,
                py: 1.5,
                borderRadius: "12px",
                textTransform: "none",
                fontWeight: 600,
                fontSize: "0.9rem",
                minHeight: "48px",
              }}
            >
              {config.actionText}
            </MDButton>
          </MDBox>
        )}

        {/* Helpful Tip */}
        {config.tip && (
          <MDBox component={motion.div} variants={itemVariants} mt={3}>
            <MDTypography
              variant="caption"
              sx={{
                color: "#7B809A",
                fontSize: "0.75rem",
                display: "block",
              }}
            >
              {config.tip}
            </MDTypography>
          </MDBox>
        )}
      </MDBox>
    </MDBox>
  );
}

EmptyState.propTypes = {
  variant: PropTypes.oneOf(["rss", "drafts", "posted", "rejected", "search"]),
  onAction: PropTypes.func,
};

export default EmptyState;
